import React from 'react';
import * as yup from 'yup';
import { Box, Button, Formbot, Form, Input, Text, styled } from '@goodrx/matisse';
import { Checkbox } from 'app/components/SignupFormCheckbox';
import { useToggledState } from 'app/hooks';
import { LegalLink } from 'app/components/links';
import { HmaCheckbox, HmaInfoModal } from 'app/components/legal';
import * as Analytics from 'app/lib/analytics';
import { Divider } from 'app/components/core/Divider';
import { CoreLink } from 'app/components/core';

interface FormValues {
  consentedToHma?: boolean;
  email?: string;
  password?: string;
  terms?: boolean;
}

export interface HandleSignUpProps {
  values: FormValues;
  isValid: boolean;
}

const validationSchema = {
  email: yup.string().email('Invalid email').required('Please enter your email.'),
  password: yup.string().required('Please enter your password.'),
  terms: yup
    .boolean()
    .oneOf([true], 'Please agree to our terms of service.')
    .required('Please agree to our terms of service.'),
  consentedToHma: yup.boolean(),
};

const StyledCoreLink = styled(CoreLink)`
  color: ${(p) => p.theme.colors.primary};
  font-weight: 500;
`;

const PasswordGuidelines = styled.ul`
  margin-left: -1.2em;
  > li {
    p {
      margin-left: 0;
    }

    &:nth-of-type(2) {
      margin-top: -3px;
    }
  }
`;

const StyledDivider = styled(Divider)`
  margin: 24px 0;
`;

interface SignUpFormProps {
  initialEmail?: string;
  onSubmit?: (props: HandleSignUpProps) => void;
  isLoading?: boolean;
  useNextLinks?: boolean;
  ctaCopy?: string;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({
  initialEmail,
  onSubmit,
  isLoading,
  useNextLinks = false,
  ctaCopy = 'Create Account',
}: SignUpFormProps) => {
  const [isHmaModalOpen, toggleHmaModal] = useToggledState(false);
  const [isTermsChecked, checkTerms] = useToggledState(false);
  const [isHmaChecked, checkHma] = useToggledState(false);

  return (
    <Formbot
      initialValues={{
        email: initialEmail,
        password: '',
        terms: false,
        consentedToHma: false,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}>
      {({ values, onChange, errors }: any) => (
        <Form>
          <Input label="Email Address" floating name="email" placeholder="Email Address" />
          <Input label="Password" floating name="password" placeholder="Password" type="password" />

          <PasswordGuidelines>
            <li>
              <Text as="p" color="greyDarkest" fontSize={14} mx={2} mt={1} fontWeight="500">
                Password must be at least 8 characters long
              </Text>
            </li>
            <li>
              <Text as="p" color="greyDarkest" fontSize={14} mx={2} mt={1} fontWeight="500">
                Please include 1 capital letter, 1 lowercase letter, and a number
              </Text>
            </li>
          </PasswordGuidelines>

          <StyledDivider />

          <Box mt={3}>
            <Checkbox
              id="terms"
              name="terms"
              error={errors.terms}
              onChange={(field: string, value: boolean) => {
                if (value && !isTermsChecked) {
                  Analytics.onTermsAgreement();
                  checkTerms();
                }
                onChange(field, value);
              }}
              label={
                <Text lineHeight="18px">
                  I agree to the{' '}
                  {useNextLinks ? (
                    <>
                      <StyledCoreLink href="/legals/terms-and-conditions" target="_blank" rel="noopener">
                        Terms of Use,
                      </StyledCoreLink>{' '}
                      <StyledCoreLink href="/legals/privacy-policy" target="_blank" rel="noopener">
                        Privacy Policy
                      </StyledCoreLink>{' '}
                      and{' '}
                      <StyledCoreLink href="/legals/consent-to-telemedicine" target="_blank" rel="noopener">
                        Telehealth Consent
                      </StyledCoreLink>
                    </>
                  ) : (
                    <>
                      <LegalLink to="/legals/terms-and-conditions" target="_blank" rel="noopener">
                        Terms of Use,
                      </LegalLink>{' '}
                      <LegalLink to="/legals/privacy-policy" target="_blank" rel="noopener">
                        Privacy Policy
                      </LegalLink>{' '}
                      and{' '}
                      <LegalLink to="/legals/consent-to-telemedicine" target="_blank" rel="noopener">
                        Telehealth Consent
                      </LegalLink>
                    </>
                  )}
                </Text>
              }
            />

            <Box mt={2}>
              <HmaCheckbox
                onLearnMoreClick={toggleHmaModal}
                value={values.consentedToHma}
                onChange={(field: string, value: boolean) => {
                  if (value && !isHmaChecked) {
                    Analytics.onHmaAgreement();
                    checkHma();
                  }
                  onChange(field, value);
                }}
                useNextLinks={useNextLinks}
              />
              <HmaInfoModal isOpen={isHmaModalOpen} onClose={toggleHmaModal} />
            </Box>
          </Box>

          <Button mt={4} mb={4} block type="submit" variant="primary" size="lg" loading={isLoading}>
            {ctaCopy}
          </Button>
        </Form>
      )}
    </Formbot>
  );
};
